import { Download, FileText, Plus, RefreshCw, SearchX, Wifi, WifiOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import StatusBadge from "@/components/ui/StatusBadge";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { MANDATE_TAB_DEFS } from "@/components/feature/mandates/constants";
import { getSelfRetailerId } from "@/components/feature/auth/session";
import CancelModal from "./components/CancelModal";
import DeleteMandateModal from "./components/DeleteMandateModal";
import FilterBar from "./components/FilterBar";
import GenerateLinkModal from "./components/GenerateLinkModal";
import { MandateMenu } from "./components/MandateMenu";
import StatusTabs from "./components/StatusTabs";
import { formatDate, formatINR, formatTime } from "./data";
import { mandateService } from "@/services/apiServices";
import type { Mandate, MandateCounts } from "@/types/mandate";
import { useMandateLiveUpdates, useRealtimeEvents } from "@/context/RealtimeEventsContext";

const DEFAULT_RANGE = "Last 30 Days";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];

function todayStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangeToDates(range: string, customFrom: string, customTo: string): { dateFrom?: string; dateTo?: string } {
  const now = new Date();
  switch (range) {
    case "Today":
      return { dateFrom: todayStr(now), dateTo: todayStr(now) };
    case "Last 7 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Last 30 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "This Month": {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { dateFrom: todayStr(from), dateTo: todayStr(to) };
    }
    case "Last 3 Months": {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 3);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Custom Range":
      return { dateFrom: customFrom || undefined, dateTo: customTo || undefined };
    default:
      return {};
  }
}

function escapeCsvValue(value: unknown): string {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function mandateCsvRows(rows: Mandate[]): string {
  const headers = ["Ref No", "Customer", "Mobile", "Bank", "Auth Mode", "EMI Amount", "Status", "Created"];
  const body = rows.map((m) => [
    m.refNo,
    m.customerName,
    m.customerMobile,
    m.bankName || m.upiId || "—",
    m.authMode,
    m.emiAmount,
    m.status,
    m.createdAt,
  ]);
  return [headers, ...body].map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const EMPTY_COUNTS: MandateCounts = {
  all: 0,
  created: 0,
  pendingAuth: 0,
  active: 0,
  paused: 0,
  halted: 0,
  cancelled: 0,
  completed: 0,
  terminated: 0,
};

export default function RetailerMandates() {
  return (
    <ToastProvider>
      <MandatesInner />
    </ToastProvider>
  );
}

function MandatesInner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const retailerId = getSelfRetailerId();
  const { isConnected, connectionStatus } = useRealtimeEvents();

  const resolveInitialTab = useCallback(() => {
    const s = searchParams.get("status");
    const t = searchParams.get("tab");
    if (
      s?.toUpperCase() === "PENDING_AUTH" ||
      s?.toUpperCase() === "PENDING" ||
      t?.toLowerCase() === "pending" ||
      t?.toLowerCase() === "pending auth" ||
      t?.toLowerCase() === "pending authentication"
    ) {
      return "Created";
    }
    if (t) {
      const match = MANDATE_TAB_DEFS.find((d) => d.key.toLowerCase() === t.toLowerCase());
      if (match) return match.key;
    }
    if (s) {
      const match = MANDATE_TAB_DEFS.find((d) => d.status === s || d.key.toUpperCase() === s.toUpperCase());
      if (match) return match.key;
    }
    return "All";
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [list, setList] = useState<Mandate[]>([]);
  const [counts, setCounts] = useState<MandateCounts>(EMPTY_COUNTS);
  const [banks, setBanks] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [tab, setTab] = useState(resolveInitialTab);

  useEffect(() => {
    const nextTab = resolveInitialTab();
    if (nextTab !== tab) {
      setTab(nextTab);
      setPage(1);
    }
  }, [resolveInitialTab]);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [bank, setBank] = useState("");
  const [mode, setMode] = useState("");
  const [sort, setSort] = useState("Newest First");

  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Mandate | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Mandate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [generateLinkOpen, setGenerateLinkOpen] = useState(false);
  const [generateLinkTarget, setGenerateLinkTarget] = useState<Mandate | null>(null);
  const [hasAnyEver, setHasAnyEver] = useState(true);

  useEffect(() => {
    mandateService
      .getMandates({ retailerId, limit: 1 })
      .then((res) => setHasAnyEver((res?.data?.pagination?.total ?? 0) > 0))
      .catch(() => setHasAnyEver(true));
  }, [retailerId]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, tab, bank, mode, range, customFrom, customTo, sort]);

  const fetchMandates = useCallback(() => {
    setLoading(true);
    const tabDef = MANDATE_TAB_DEFS.find((t) => t.key === tab);
    const { dateFrom, dateTo } = rangeToDates(range, customFrom, customTo);
    mandateService
      .getMandates({
        retailerId,
        page,
        limit: pageSize,
        search,
        status: tabDef?.status ?? undefined,
        bank: bank || undefined,
        authMode: mode || undefined,
        dateFrom,
        dateTo,
        sort,
      })
      .then((res) => {
        const data = res?.data;
        setList(data?.mandates ?? []);
        setCounts(data?.counts ?? EMPTY_COUNTS);
        setBanks(data?.banks ?? []);
        setTotal(data?.pagination?.total ?? 0);
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to load mandates");
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [retailerId, page, pageSize, search, tab, bank, mode, range, customFrom, customTo, sort, toast]);

  useEffect(() => {
    fetchMandates();
  }, [fetchMandates]);

  // Debounce ref — prevents burst webhooks from triggering multiple simultaneous re-fetches
  const debouncedFetchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedFetch = useCallback(() => {
    if (debouncedFetchRef.current) clearTimeout(debouncedFetchRef.current);
    debouncedFetchRef.current = setTimeout(() => fetchMandates(), 600);
  }, [fetchMandates]);

  // ─── [REAL-TIME SSE] Instant Table Update ─────────────────────────────────
  // Primary update path: When a Webhook arrives at the backend, SSE pushes the
  // event here. We update the matching table row in-place (O(1) client update),
  // giving the user instant feedback without needing to re-fetch the entire list.
  useMandateLiveUpdates((payload) => {
    const status = payload?.status || payload?.mandate?.status;
    const mandateId = payload?.mandateId || payload?.mandate?.id;
    const refNo = payload?.refNo || payload?.mandate?.refNo;

    if (status && (mandateId || refNo)) {
      // Optimistic in-place update — no server round-trip needed
      setList((prev) =>
        prev.map((r) =>
          r.id === mandateId || r.refNo === refNo
            ? { ...r, status, ...(payload.mandate || {}) }
            : r
        )
      );
      toast.success(
        `Live update: ${refNo || mandateId} is now ${String(status).replace(/_/g, " ")}`
      );
    } else {
      // Fallback: debounced re-fetch if payload doesn't contain enough info
      debouncedFetch();
    }
  }, undefined, retailerId);

  // ─── [FALLBACK POLLING] Offline Auto-Sync (Every 60s) ──────────────────────
  // Activates ONLY when SSE stream is disconnected (`!isConnected`).
  // Guarantees the list eventually refreshes even if SSE or Webhooks are down.
  // Pauses automatically as soon as SSE reconnects.
  useEffect(() => {
    if (isConnected) return;
    const interval = setInterval(() => {
      fetchMandates();
    }, 60_000);
    return () => clearInterval(interval);
  }, [isConnected, fetchMandates]);

  const tabItems = useMemo(
    () =>
      MANDATE_TAB_DEFS.map((t) => ({
        key: t.key,
        label: t.label,
        value: t.status
          ? counts[(t.key === "Pending Auth" ? "pendingAuth" : (t.key.toLowerCase() as keyof MandateCounts))] ?? 0
          : counts.all,
      })),
    [counts]
  );

  const chips = useMemo(() => {
    const c: { label: string; onRemove: () => void }[] = [];
    if (searchInput.trim()) c.push({ label: `Search: ${searchInput}`, onRemove: () => setSearchInput("") });
    if (bank) c.push({ label: `Bank: ${bank}`, onRemove: () => setBank("") });
    if (mode) c.push({ label: `Mode: ${mode}`, onRemove: () => setMode("") });
    if (sort !== "Newest First") c.push({ label: `Sort: ${sort}`, onRemove: () => setSort("Newest First") });
    if (range !== DEFAULT_RANGE) c.push({ label: `Range: ${range}`, onRemove: () => setRange(DEFAULT_RANGE) });
    if (range === "Custom Range" && customFrom) c.push({ label: `From: ${customFrom}`, onRemove: () => setCustomFrom("") });
    if (range === "Custom Range" && customTo) c.push({ label: `To: ${customTo}`, onRemove: () => setCustomTo("") });
    return c;
  }, [searchInput, bank, mode, sort, range, customFrom, customTo]);

  function resetFilters() {
    setSearchInput("");
    setRange(DEFAULT_RANGE);
    setCustomFrom("");
    setCustomTo("");
    setBank("");
    setMode("");
    setSort("Newest First");
  }

  function openDetail(m: Mandate) {
    navigate(`/retailer/mandates/${m.refNo}`);
  }

  function copyLink(m: Mandate) {
    const url = m.checkoutUrl || (m.authLink?.startsWith("http") ? m.authLink : `${window.location.origin}${m.authLink}`);
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Auth link copied to clipboard"))
      .catch(() => toast.error("Could not copy auth link"));
  }

  function refreshStatus(m: Mandate) {
    const idToSync = m.refNo || m.id;
    if (!idToSync) return;
    setRefreshingId(m.id);
    mandateService
      .syncWithRocketPay(idToSync)
      .then((res) => {
        const updated: Mandate | undefined = res?.data?.mandate || res?.data;
        if (updated && updated.status) {
          setList((prev) =>
            prev.map((r) =>
              r.id === m.id || r.refNo === m.refNo
                ? { ...r, ...updated, status: updated.status }
                : r
            )
          );
          toast.success(`Status updated: ${updated.status.replace("_", " ")}`);
        } else {
          fetchMandates();
          toast.success(res?.message || "Status refreshed from gateway");
        }
      })
      .catch((err) => toast.error(err?.message || "Failed to refresh status from gateway"))
      .finally(() => setRefreshingId(null));
  }

  function openCancel(m: Mandate) {
    setCancelTarget(m);
    setCancelOpen(true);
  }

  function openDelete(m: Mandate) {
    setDeleteTarget(m);
    setDeleteOpen(true);
  }

  function openGenerateLink(m: Mandate) {
    setGenerateLinkTarget(m);
    setGenerateLinkOpen(true);
  }

  function handleEditDraft(m: Mandate) {
    navigate(`/retailer/create-mandate?draftId=${m.id}`);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    if (deleteTarget.status !== "CREATED" && deleteTarget.status !== "DRAFT") {
      toast.error("Delete is only allowed when mandate is in CREATED or DRAFT state");
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await mandateService.delete(deleteTarget.id);
      toast.success(res?.message || "Mandate deleted");
      setDeleteOpen(false);
      setDeleteTarget(null);
      fetchMandates();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete mandate");
    } finally {
      setDeleteLoading(false);
    }
  }

  async function confirmCancel(reason: string, remarks: string) {
    if (!cancelTarget) return;
    if (cancelTarget.status !== "ACTIVE" && cancelTarget.status !== "ACTIVATED") {
      toast.error("Cancel is only allowed when mandate is in ACTIVATED state");
      return;
    }
    setCancelLoading(true);
    try {
      const res = await mandateService.cancel(cancelTarget.id, reason, remarks);
      toast.success(res?.message || "Mandate cancelled successfully");
      setCancelOpen(false);
      setCancelTarget(null);
      fetchMandates();
    } catch (err: any) {
      toast.error(err?.message || "Failed to cancel mandate");
    } finally {
      setCancelLoading(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const tabDef = MANDATE_TAB_DEFS.find((t) => t.key === tab);
      const { dateFrom, dateTo } = rangeToDates(range, customFrom, customTo);
      const res = await mandateService.getMandates({
        retailerId,
        page: 1,
        limit: 10000,
        search,
        status: tabDef?.status ?? undefined,
        bank: bank || undefined,
        authMode: mode || undefined,
        dateFrom,
        dateTo,
        sort,
      });
      const rows: Mandate[] = res?.data?.mandates ?? [];
      if (rows.length === 0) {
        toast.error("No mandates to export");
        return;
      }
      downloadCsv(mandateCsvRows(rows), `my-mandates-export-${Date.now()}.csv`);
      toast.success(`Exported ${rows.length} mandate${rows.length === 1 ? "" : "s"} successfully`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to export mandates");
    } finally {
      setExporting(false);
    }
  }

  function exportSelected() {
    const rows = list.filter((r) => selectedKeys.includes(r.id));
    if (rows.length === 0) {
      toast.error("No mandates selected");
      return;
    }
    downloadCsv(mandateCsvRows(rows), `my-mandates-selected-${Date.now()}.csv`);
    toast.success(`Exported ${rows.length} mandate${rows.length === 1 ? "" : "s"} successfully`);
  }

  const columns: Column<Mandate>[] = [
    {
      key: "refNo",
      header: "Ref No",
      width: "140px",
      render: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openDetail(row);
          }}
          className="text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
        >
          {row.refNo}
        </button>
      ),
    },
    {
      key: "customerName",
      header: "Customer",
      width: "180px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{row.customerName}</p>
          <p className="text-xs text-ink-secondary">{row.customerMobile}</p>
        </div>
      ),
    },
    {
      key: "product",
      header: "Product",
      width: "160px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">
            {[row.brand, row.model].filter(Boolean).join(" ") || "—"}
          </p>
          <p className="truncate text-xs text-ink-secondary">{row.category ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "bankName",
      header: "Bank / UPI",
      width: "160px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{row.bankName || row.upiId || "—"}</p>
          <p className="text-xs text-ink-secondary">{row.accountNumber || ""}</p>
        </div>
      ),
    },
    {
      key: "amount",
      header: "EMI",
      width: "110px",
      align: "right",
      render: (row) => (
        <div className="text-right">
          <p className="text-sm font-medium text-ink">{formatINR(row.emiAmount)}</p>
          <p className="text-xs text-ink-secondary">{row.tenureMonths} months</p>
        </div>
      ),
    },
    {
      key: "authMode",
      header: "Auth Mode",
      width: "110px",
      render: (row) => <span className="whitespace-nowrap text-[13px] text-ink">{row.authMode}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "135px",
      render: (row) => {
        const isSyncing = refreshingId === row.id || refreshingId === row.refNo;
        return (
          <div className="inline-flex items-center gap-1.5">
            <StatusBadge status={row.status} />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                refreshStatus(row);
              }}
              disabled={isSyncing}
              title="Fetch latest status from gateway"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-primary-600 hover:border-primary-300 shadow-sm transition-all cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${isSyncing ? "animate-spin text-primary-600" : ""}`} />
            </button>
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Created",
      width: "120px",
      render: (row) => (
        <div>
          <p className="whitespace-nowrap text-[13px] text-ink">{formatDate(row.createdAt)}</p>
          <p className="text-xs text-ink-secondary">{formatTime(row.createdAt)}</p>
        </div>
      ),
    },
  ];

  function rowActions(row: Mandate) {
    return (
      <MandateMenu
        row={row}
        refreshing={refreshingId === row.id}
        onView={() => openDetail(row)}
        onCopy={() => copyLink(row)}
        onPreview={() => window.open(row.checkoutUrl || row.authLink, "_blank", "noopener,noreferrer")}
        onRefresh={() => refreshStatus(row)}
        onDownload={() => toast.info("Open the mandate to download its receipt")}
        onCancel={() => openCancel(row)}
        onDelete={() => openDelete(row)}
        onGenerateLink={() => openGenerateLink(row)}
        onEditDraft={() => handleEditDraft(row)}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">My Mandates</h1>
          <p className="mt-1 text-sm text-ink-secondary">View and manage all mandates you have created</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={handleExport} loading={exporting} leftIcon={<Download className="h-4 w-4" />}>
            Export
          </Button>
          <Button onClick={() => navigate("/retailer/create-mandate")} leftIcon={<Plus className="h-4 w-4" />}>
            Create Mandate
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <StatusTabs tabs={tabItems} active={tab} onChange={setTab} />
          {/* Real-time connection status indicator */}
          <div
            title={connectionStatus === "connected" ? "Live updates active" : connectionStatus === "connecting" ? "Connecting..." : "Offline — polling every 60s"}
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
              connectionStatus === "connected"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : connectionStatus === "connecting"
                ? "border-amber-200 bg-amber-50 text-amber-700"
                : "border-slate-200 bg-slate-50 text-slate-500"
            }`}
          >
            {connectionStatus === "connected" ? (
              <Wifi className="h-3 w-3" />
            ) : (
              <WifiOff className="h-3 w-3" />
            )}
            {connectionStatus === "connected" ? "Live" : connectionStatus === "connecting" ? "Connecting" : "Offline"}
          </div>
        </div>

        <FilterBar
          search={searchInput}
          onSearch={setSearchInput}
          range={range}
          onRange={setRange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFrom={setCustomFrom}
          onCustomTo={setCustomTo}
          bank={bank}
          onBank={setBank}
          banks={banks}
          mode={mode}
          onMode={setMode}
          sort={sort}
          onSort={setSort}
          onReset={resetFilters}
          chips={chips}
        />

        {loading ? (
          <DataTable columns={columns} data={[]} rowKey={(r) => r.id} loading selectable />
        ) : list.length === 0 ? (
          hasAnyEver ? (
            <EmptyState
              icon={SearchX}
              title="No mandates found"
              description="Try adjusting your filters or search term."
              actionLabel="Reset Filters"
              onAction={resetFilters}
            />
          ) : (
            <EmptyState
              icon={FileText}
              title="No mandates yet"
              description="Create your first mandate to start tracking customer authorisations here."
              actionLabel="Create Mandate"
              onAction={() => navigate("/retailer/create-mandate")}
            />
          )
        ) : (
          <DataTable
            columns={columns}
            data={list}
            rowKey={(r) => r.id}
            selectable
            actions={rowActions}
            onRowClick={openDetail}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            maxHeight="520px"
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            serverPagination={{
              page,
              pageSize,
              total,
              onPageChange: setPage,
              onPageSizeChange: (n) => {
                setPageSize(n);
                setPage(1);
              },
            }}
            selectionBar={(count, clear) => (
              <div className="flex flex-wrap items-center justify-between gap-3 bg-primary-50 px-4 py-3">
                <p className="text-sm font-medium text-primary-600">{count} selected</p>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={exportSelected}>
                    Export Selected
                  </Button>
                  <Button variant="ghost" size="sm" onClick={clear}>
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}
          />
        )}
      </div>

      <CancelModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        refNo={cancelTarget?.refNo ?? ""}
        onConfirm={confirmCancel}
        loading={cancelLoading}
      />

      <DeleteMandateModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        refNo={deleteTarget?.refNo ?? ""}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        isDraft={deleteTarget?.status === "DRAFT"}
      />

      <GenerateLinkModal
        open={generateLinkOpen}
        onClose={() => setGenerateLinkOpen(false)}
        mandate={generateLinkTarget}
        onSuccess={() => {
          fetchMandates();
        }}
      />
    </div>
  );
}
